import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Glasses, Wordmark } from './components/Brand';
import { ProductScreen } from './components/ProductScreen';
import { siteConfig } from './config';
import { useI18n } from './i18n';

const asset = (path: string) => `${import.meta.env.BASE_URL}${path}`;

function LanguageSwitch() {
  const { locale, setLocale, copy } = useI18n();
  return (
    <div className="language-switch" role="group" aria-label={copy.a11y.language}>
      {(['es', 'en'] as const).map((lang) => (
        <button key={lang} type="button" onClick={() => setLocale(lang)} aria-pressed={locale === lang}>{lang.toUpperCase()}</button>
      ))}
    </div>
  );
}

function Navigation() {
  const { copy } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const previous = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    panelRef.current?.querySelector<HTMLElement>('a,button')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key === 'Tab' && panelRef.current) {
        const items = [...panelRef.current.querySelectorAll<HTMLElement>('a,button:not([disabled])')];
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous.overflow;
      document.body.style.position = previous.position;
      document.body.style.top = previous.top;
      document.body.style.width = previous.width;
      window.scrollTo(0, scrollY);
      document.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  const links = [
    ['#how', copy.nav.how], ['#buyers', copy.nav.buyers], ['#sellers', copy.nav.sellers], ['#app', copy.nav.app],
  ];
  const close = () => setOpen(false);

  return (
    <header className={`site-header${scrolled ? ' is-scrolled' : ''}`}>
      <nav className="nav shell" aria-label={copy.a11y.nav}>
        <a className="nav__brand" href="#top" aria-label={copy.a11y.logo}><Wordmark /></a>
        <div className="nav__desktop">
          <div className="nav__links">{links.map(([href, label]) => <a href={href} key={href}>{label}</a>)}</div>
          <LanguageSwitch />
          <a className="button button--small" href="#early-access">{copy.nav.cta}</a>
        </div>
        <button ref={triggerRef} className="menu-trigger" type="button" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="mobile-menu" aria-label={copy.a11y.openMenu}>
          <span /><span />
        </button>
      </nav>
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div className="mobile-menu" id="mobile-menu" role="dialog" aria-modal="true" aria-label={copy.a11y.openMenu} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onPointerDown={(event) => { if (event.target === event.currentTarget) close(); }}>
              <motion.div ref={panelRef} className="mobile-menu__panel" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 260 }}>
                <div className="mobile-menu__head"><Wordmark /><button type="button" onClick={close} aria-label={copy.a11y.closeMenu}>×</button></div>
                <div className="mobile-menu__links">{links.map(([href, label]) => <a href={href} onClick={close} key={href}>{label}<span>↘</span></a>)}</div>
                <LanguageSwitch />
                <a className="button" href="#early-access" onClick={close}>{copy.nav.cta}</a>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </header>
  );
}

function HeroVisual() {
  const { copy } = useI18n();
  const reduceMotion = useReducedMotion();
  const offers = [copy.hero.offerOne, copy.hero.offerTwo, copy.hero.offerThree];
  const origins = [{ x: -110, y: -35 }, { x: 110, y: -55 }, { x: 120, y: 35 }];
  return (
    <div className="hero-visual" aria-label={copy.hero.offersLabel}>
      <span className="hero-scene__label">● {copy.hero.offersLabel}</span>
      <div className="hero-scene__halo" aria-hidden="true" />
      <motion.div className="hero-person" aria-hidden="true" animate={reduceMotion ? undefined : { y: [0, -5, 0] }} transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}>
        <div className="hero-person__head">
          <div className="hero-person__hair" />
          <i className="hero-person__ear hero-person__ear--left" /><i className="hero-person__ear hero-person__ear--right" />
          <Glasses className="hero-person__glasses" />
          <svg className="hero-person__smile" viewBox="0 0 40 22"><path d="M3 3c4 20 30 20 34 0" /></svg>
        </div>
        <div className="hero-person__neck" />
        <div className="hero-person__body">
          <i className="hero-person__arm hero-person__arm--left" /><i className="hero-person__arm hero-person__arm--right" />
          <div className="hero-person__phone"><span>{copy.hero.requestLabel}</span><strong>{copy.hero.requestTitle}</strong><small>{copy.hero.requestBudget}</small><b>↑</b></div>
        </div>
      </motion.div>
      {offers.map((price, index) => (
        <motion.div className={`hero-bid hero-bid--${index + 1}${index === 0 ? ' is-best' : ''}`} key={price} initial={reduceMotion ? false : { opacity: 0, x: origins[index].x, y: origins[index].y, scale: .72, rotate: index === 0 ? -8 : 8 }} animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: index === 0 ? -2 : index === 1 ? 3 : 1 }} transition={{ delay: reduceMotion ? 0 : .65 + index * .28, type: 'spring', stiffness: 120, damping: 16 }}>
          <span className={`hero-bid__seller hero-bid__seller--${index + 1}`} aria-hidden="true"><i /><b /></span>
          <span><strong>{price}</strong><small>{index === 0 ? copy.hero.best : 'MXN'}</small></span>
          <i aria-hidden="true">↘</i>
        </motion.div>
      ))}
      <motion.div className="hero-notification" initial={reduceMotion ? false : { opacity: 0, scale: .5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: reduceMotion ? 0 : 1.55, type: 'spring' }}><strong>3</strong><span>{copy.hero.offersLabel}</span></motion.div>
    </div>
  );
}

function Hero() {
  const { copy } = useI18n();
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, .18], [0, 70]);
  return (
    <section className="hero" id="top">
      <div className="hero__wash" aria-hidden="true" />
      <div className="shell hero__grid">
        <motion.div className="hero__copy" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .65 }}>
          <span className="eyebrow">{copy.hero.eyebrow}</span>
          <h1>{copy.hero.headlineA}<br /><span>{copy.hero.headlineB}</span><br />{copy.hero.headlineC}</h1>
          <p>{copy.hero.description}</p>
          <div className="hero__actions"><a className="button" href="#early-access">{copy.hero.primary}</a><a className="text-link" href="#how">{copy.hero.secondary}<span>↓</span></a></div>
          <div className="launch-pill"><span aria-hidden="true" />{copy.hero.launch}</div>
        </motion.div>
        <motion.div className="hero__art" style={{ y }}><HeroVisual /></motion.div>
      </div>
      <a className="scroll-cue" href="#problem" aria-label={copy.a11y.scroll}><Glasses mood="down" /><span>↓</span></a>
    </section>
  );
}

function Problem() {
  const { copy } = useI18n();
  return (
    <section className="section problem" id="problem">
      <div className="shell">
        <div className="section-heading section-heading--wide"><span className="eyebrow">{copy.problem.eyebrow}</span><h2>{copy.problem.headline}</h2><p>{copy.problem.description}</p></div>
        <div className="problem-transform">
          <motion.div className="listing-chaos" initial="hidden" whileInView="show" viewport={{ once: true, amount: .35 }} variants={{ show: { transition: { staggerChildren: .07 } } }}>
            {copy.problem.chaos.map((item, index) => <motion.div key={item} variants={{ hidden: { opacity: 0, rotate: index % 2 ? 7 : -7, y: 18 }, show: { opacity: .62, rotate: index % 2 ? 3 : -3, y: 0 } }}><span>{index % 2 ? '□' : '○'}</span>{item}</motion.div>)}
          </motion.div>
          <div className="transform-arrow" aria-hidden="true"><span>→</span><Glasses mood="cta" /></div>
          <motion.div className="clarity-card" initial={{ opacity: 0, scale: .92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: .5 }}>
            <span className="micro-label">{copy.problem.clarity}</span><strong>{copy.problem.example.title}</strong><p>{copy.problem.example.meta}</p><div><b>3</b> {copy.problem.relevant}</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const screens = {
  request: asset('screens/create-request.png'),
  offers: asset('screens/offers.png'),
  chat: asset('screens/chat.png'),
  camera: asset('screens/live-camera.png'),
  captured: asset('screens/live-captured.png'),
  feed: asset('screens/request-feed.png'),
  detail: asset('screens/offer-detail.png'),
};

function HowItWorks() {
  const { copy } = useI18n();
  const paths = [screens.request, screens.offers, screens.chat];
  const alts = [copy.showcase.screenAlts.request, copy.showcase.screenAlts.offers, copy.showcase.screenAlts.chat];
  return (
    <section className="section how" id="how">
      <div className="shell"><div className="section-heading"><span className="eyebrow">{copy.how.eyebrow}</span><h2>{copy.how.headline}</h2><p>{copy.how.description}</p></div></div>
      <div className="shell how__chapters">
        {copy.how.steps.map((step, index) => <motion.article className={`how-chapter how-chapter--${index + 1}`} key={step.number} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: .18 }} transition={{ duration: .55, delay: index * .1 }}>
          <div className="how-chapter__copy"><span>{step.number}</span><h3>{step.title}</h3><p>{step.body}</p></div>
          <div className="how-chapter__screen"><ProductScreen src={paths[index]} alt={alts[index]} eager={index === 0} /></div>
        </motion.article>)}
      </div>
    </section>
  );
}

function Showcase() {
  const { copy } = useI18n();
  return (
    <section className="section showcase" id="app">
      <div className="shell showcase__grid">
        <div className="showcase__copy"><span className="eyebrow">{copy.showcase.eyebrow}</span><h2>{copy.showcase.headline}</h2><p>{copy.showcase.description}</p>
          <div className="feature-list">{copy.showcase.items.map((item, index) => <motion.article key={item.title} initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * .08 }}><span>{String(index + 1).padStart(2, '0')}</span><div><h3>{item.title}</h3><p>{item.body}</p></div></motion.article>)}</div>
        </div>
        <div className="decision-scene">
          <div className="decision-scene__rings" aria-hidden="true" />
          <ProductScreen src={screens.detail} alt={copy.showcase.screenAlts.detail} className="phone--decision" />
          <div className="decision-notes" aria-hidden="true">{copy.showcase.items.map((item, index) => <motion.div className={`decision-note decision-note--${index + 1}`} key={item.title} initial={{ opacity: 0, x: index === 1 ? 24 : -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: .2 + index * .12 }}><span>{String(index + 1).padStart(2, '0')}</span><strong>{item.title}</strong></motion.div>)}</div>
          <div className="decision-eyes"><Glasses mood="cta" /></div>
        </div>
      </div>
    </section>
  );
}

function LivePhoto() {
  const { copy } = useI18n();
  return (
    <section className="section live-photo">
      <div className="shell live-photo__grid">
        <div className="live-photo__screens"><ProductScreen src={screens.camera} alt={copy.live.cameraAlt} className="phone--camera" /><ProductScreen src={screens.captured} alt={copy.live.resultAlt} className="phone--captured" /><motion.div className="live-stamp" initial={{ scale: .6, rotate: -10, opacity: 0 }} whileInView={{ scale: 1, rotate: -5, opacity: 1 }} viewport={{ once: true }}>● {copy.live.capture}</motion.div></div>
        <div className="live-photo__copy"><span className="eyebrow">{copy.live.eyebrow}</span><h2>{copy.live.headline}<br /><span>{copy.live.accent}</span></h2><p>{copy.live.description}</p><div className="trust-row"><span>◷</span><strong>{copy.live.timestamp}</strong></div><small>{copy.live.note}</small></div>
      </div>
    </section>
  );
}

function Audiences() {
  const { copy } = useI18n();
  const panels = [{ id: 'buyers', data: copy.audiences.buyers, accent: 'purple' }, { id: 'sellers', data: copy.audiences.sellers, accent: 'orange' }] as const;
  return (
    <section className="section audiences"><div className="shell"><div className="section-heading section-heading--wide"><span className="eyebrow">{copy.audiences.eyebrow}</span><h2>{copy.audiences.headline}</h2></div>
      <div className="audience-grid">{panels.map(({ id, data, accent }) => <motion.article id={id} className={`audience-card audience-card--${accent}`} key={id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: .3 }}><span className="micro-label">{data.label}</span><h3>{data.title}</h3><ul>{data.items.map((item) => <li key={item}><span>✓</span>{item}</li>)}</ul>{id === 'buyers' ? <div className="audience-request">{copy.audiences.buyerPreview} <b>→</b></div> : <div className="seller-request" aria-hidden="true"><div className="seller-request__top"><span>{copy.audiences.sellerPreview.badge}</span><i>●</i></div><strong>{copy.audiences.sellerPreview.title}</strong><small>{copy.audiences.sellerPreview.meta}</small><div className="seller-request__action">{copy.audiences.sellerPreview.action}<b>→</b></div></div>}</motion.article>)}</div>
    </div></section>
  );
}

function City() {
  const { copy } = useI18n();
  return (
    <section className="section city"><div className="shell city__grid"><div className="city__copy"><span className="eyebrow">{copy.city.eyebrow}</span><h2>{copy.city.headline}</h2><p>{copy.city.description}</p><span className="launch-pill"><span />{copy.city.badge}</span></div>
      <div className="city-map" aria-hidden="true"><div className="map-ring map-ring--one"/><div className="map-ring map-ring--two"/><Glasses />{copy.city.labels.map((label, index) => <motion.span key={label} className={`map-pin map-pin--${index + 1}`} initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: index * .1, type: 'spring' }}>{label}</motion.span>)}</div>
    </div></section>
  );
}

function FinalCta() {
  const { copy } = useI18n();
  const mailto = `mailto:${siteConfig.contactEmail}?subject=${encodeURIComponent(copy.waitlist.contactSubject)}`;
  return (
    <section className="final-cta" id="early-access"><div className="shell final-cta__grid"><div className="final-cta__copy"><span className="eyebrow">{copy.waitlist.eyebrow}</span><h2>{copy.waitlist.headline}</h2><p>{copy.waitlist.description}</p></div><div className="contact-card"><div className="contact-card__brand"><Wordmark /></div><h3>{copy.waitlist.contactTitle}</h3><p>{copy.waitlist.contactBody}</p><a className="button contact-card__button" href={mailto}>{copy.waitlist.contactCta}<span aria-hidden="true">↗</span></a><a className="contact-card__email" href={mailto}>{siteConfig.contactEmail}</a></div></div></section>
  );
}

function Footer() {
  const { copy } = useI18n();
  return <footer className="footer"><div className="shell footer__grid"><div><Wordmark light /><p>{copy.footer.tagline}</p></div><div><p>{copy.footer.location}</p><span>{copy.footer.rights}</span></div><div className="footer__links">{siteConfig.links.privacy && <a href={siteConfig.links.privacy}>{copy.footer.privacy}</a>}{siteConfig.links.terms && <a href={siteConfig.links.terms}>{copy.footer.terms}</a>}</div></div></footer>;
}

export function App() {
  const { copy } = useI18n();
  return (
    <>
      <a className="skip-link" href="#main">{copy.a11y.skip}</a>
      <Navigation />
      <main id="main"><Hero /><Problem /><HowItWorks /><Showcase /><LivePhoto /><Audiences /><City /><FinalCta /></main>
      <Footer />
    </>
  );
}
